import { createSlice, PayloadAction } from '@reduxjs/toolkit'

declare global {
  interface Window {
    process?: {
      env?: {
        REACT_APP_USE_STORED_STATE?: string
      }
    }
    formData?: FormDataState
  }
}

window.process = {
  ...window.process,
}

interface FamilyMember {
  type: 'adult' | 'child'
  set: boolean
  id: string
  subscription: boolean
  [key: string]: any
}

interface Family {
  completed: boolean
  digitalSafety: boolean | null
  ownerWantsSubscription: boolean
  counts: {
    child: number
    adult: number
  }
  members: FamilyMember[]
  [key: string]: any
}

export interface FormDataState {
  step: string
  familyStep: string
  family: Family
  bulkInvoiceDiscountActive: boolean
  newPhoneSession: boolean
  newFamilyPhoneSession: boolean
  newPowerSession: boolean
  singleExistingOwnerPhoneNo: string | null
  phoneItems: any[]
  powerItems: any[]
  ssn: string
  firstName: string
  lastName: string
  email: string
  phone: string
  address: string
  postalCode: string
  postalArea: string
  dateOfBirth: string | null
  signature: any
  currentPhone: any
  operatorId: any
  acceptBoxes: any[]
  isSubmitting: boolean
  combinedInvoice: any
  departmentId: string | null
  isWaitingForSMS: boolean
  smsVerified: boolean
  importResult: any
  verificationId: any
  clientId: any
  serverId: any
  saleRef: any
  [key: string]: any
}

let storedState: FormDataState | undefined
if (import.meta.env.VITE_USE_STORED_STATE) {
  try {
    const fullState = window.localStorage.getItem('_state')
    if (fullState) {
      storedState = JSON.parse(fullState).formData
    }
  } catch (e) {
    console.error('Unable to set initial state from local storage:', e)
  }
}

const pristineState: FormDataState = {
  step: 'add-subs',
  familyStep: 'package',
  family: {
    completed: false,
    digitalSafety: null,
    ownerWantsSubscription: true,
    counts: {
      child: 0,
      adult: 2,
    },
    members: [
      {
        type: 'adult',
        set: false,
        id: Math.random().toString(),
        subscription: true,
      },
      {
        type: 'adult',
        set: false,
        id: Math.random().toString(),
        subscription: true,
      },
    ],
  },
  bulkInvoiceDiscountActive: false,
  newPhoneSession: false,
  newFamilyPhoneSession: false,
  newPowerSession: false,
  singleExistingOwnerPhoneNo: null,
  phoneItems: [],
  powerItems: [],
  ssn: '',
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  address: '',
  postalCode: '',
  postalArea: '',
  dateOfBirth: null,
  signature: null,
  currentPhone: null,
  operatorId: null,
  acceptBoxes: [],
  isSubmitting: false,
  combinedInvoice: null,
  departmentId: null,
  isWaitingForSMS: false,
  smsVerified: false,
  importResult: null,
  verificationId: null,
  clientId: null,
  serverId: null,
  saleRef: null,
}

const initialState: FormDataState = storedState || pristineState

const formDataSlice = createSlice({
  name: 'formData',
  initialState,
  reducers: {
    setFormData: (state, action: PayloadAction<Partial<FormDataState>>) => {
      const newFormData = { ...state, ...action.payload }
      window.formData = newFormData
      return newFormData
    },
    resetFormData: (state) => {
      try {
        window.localStorage.removeItem('_state')
      } catch (e) {
        console.error('Unable to remove local storage state:', e)
      }
      storedState = undefined
      const newState = { ...pristineState }
      console.log('== RESET NOW', newState)
      return newState
    },
  },
})

export const { setFormData, resetFormData } = formDataSlice.actions
export default formDataSlice.reducer